import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { managerApi, type BannedAssistant } from '../../api/manager_api';

interface Props {
  userKey: string;
  clientName: string;
}

/** Assistants this client never wants to be matched with. A banned
 * assistant's claim on the client's tasks is refused. */
export const ManagerUserBannedAssistants = ({ userKey, clientName }: Props) => {
  const [bans, setBans] = useState<BannedAssistant[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);

  useEffect(() => {
    setBans(null);
    managerApi.getBannedAssistants(userKey)
      .then(setBans)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load bans'));
  }, [userKey]);

  const handleAdd = async () => {
    setAdding(true);
    setError(null);
    try {
      const ban = await managerApi.banAssistant(userKey, email.trim(), reason.trim());
      setBans((prev) => [ban, ...(prev ?? [])]);
      setEmail('');
      setReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to ban assistant');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (banKey: string) => {
    setRemoving(banKey);
    setError(null);
    try {
      await managerApi.unbanAssistant(userKey, banKey);
      setBans((prev) => (prev ?? []).filter((b) => b.key !== banKey));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove ban');
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Assistants {clientName} has banned
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These assistants can't claim {clientName}'s tasks.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {bans === null && !error ? (
          <CircularProgress size={24} />
        ) : bans && bans.length > 0 ? (
          <List dense disablePadding sx={{ mb: 2 }}>
            {bans.map((ban) => (
              <ListItem
                key={ban.key}
                divider
                disableGutters
                secondaryAction={
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleRemove(ban.key)}
                    disabled={removing === ban.key}
                  >
                    Remove
                  </Button>
                }
              >
                <ListItemText
                  primary={`${ban.assistant_name || ban.assistant_email || 'Unknown assistant'}${
                    ban.assistant_email && ban.assistant_name ? ` (${ban.assistant_email})` : ''
                  }`}
                  secondary={[
                    ban.reason,
                    ban.date_created && `banned ${new Date(ban.date_created).toLocaleDateString()}`,
                  ].filter(Boolean).join(' · ')}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {clientName} hasn't banned any assistants.
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Assistant email"
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ flex: '1 1 220px' }}
          />
          <TextField
            label="Reason"
            size="small"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ flex: '2 1 260px' }}
          />
          <Button
            variant="outlined"
            onClick={handleAdd}
            disabled={adding || !email.trim()}
          >
            Ban assistant
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
